import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCSATWordFrequency } from '@/hooks/useCSATWordFrequency';
import { ResponsiveBar } from '@nivo/bar';
import { MessageSquare } from 'lucide-react';
import { statusColors } from '@/theme/colors';

interface WordFrequencyChartProps {
  daysRange: number;
}

export const WordFrequencyChart = ({ daysRange }: WordFrequencyChartProps) => {
  const { data, isLoading } = useCSATWordFrequency(daysRange, 15);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Temas Mais Citados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Temas Mais Citados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            <p>Nenhum comentário disponível para análise</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map(item => ({
    word: item.word,
    count: item.count,
    frequency: item.frequency,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Temas Mais Citados nos Comentários
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="w-full min-h-[300px] max-h-[400px] overflow-hidden">
          <div className="w-full h-[300px] sm:h-[350px] md:h-[400px]">
            <ResponsiveBar
              data={chartData}
              keys={['count']}
              indexBy="word"
              margin={{ top: 20, right: 20, bottom: 80, left: 50 }}
            padding={0.3}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={() => statusColors.success}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Palavras',
              legendPosition: 'middle',
              legendOffset: 50,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Frequência',
              legendPosition: 'middle',
              legendOffset: -40,
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            tooltip={({ value, indexValue }) => (
              <div className="bg-white p-2 rounded shadow-lg border">
                <div className="font-semibold">{indexValue}</div>
                <div className="text-sm text-muted-foreground">
                  {value} {value === 1 ? 'ocorrência' : 'ocorrências'}
                </div>
              </div>
            )}
            animate={true}
            motionConfig="gentle"
            />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {data.slice(0, 6).map((item, index) => (
            <div
              key={item.word}
              className="p-2 rounded border bg-muted/50 text-center"
            >
              <div className="text-xs text-muted-foreground mb-1">
                #{index + 1}
              </div>
              <div className="font-semibold text-sm mb-1">
                {item.word}
              </div>
              <div className="text-xs text-muted-foreground">
                {item.count} {item.count === 1 ? 'vez' : 'vezes'}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

